import React, { useMemo } from 'react'
import Dialog from '@src/components/dialog'
import classes from '@src/styles/disposal/chat-calendly-modal.module.css'
import useCalendlyMessages from '@src/hooks/useCalendlyMessages'
import { buildCalendlyUrl, eventIdFromUri } from '@src/functions/calendly'
import { getAnonymousId, segmentTrack } from '@src/functions/segment'
import reportChatMeetingBooked from '@src/functions/chat-meeting'

/** 620px is the height the booking app's own Calendly step uses. */
const CALENDLY_HEIGHT = 620

// Stored on the Calendly invitee, so a booking can be traced to its chat session from
// Calendly's side as well as through the booking report.
const UTM_SOURCE = 'dimension-chat'

export type ChatCalendlyModalProps = {
  /** The booking link Lindy sent, as rendered in the chat. Null keeps the dialog closed. */
  link: string | null
  sessionId: string
  service?: string
  onClose: () => void
}

/**
 * The AE's Calendly, opened in place when the customer clicks the booking link in the chat.
 * Embedding it (rather than letting the link open calendly.com) is what lets the page see
 * `calendly.event_scheduled` and report the booking — the chat itself never learns of it.
 */
export default function ChatCalendlyModal({
  link,
  sessionId,
  service,
  onClose,
}: ChatCalendlyModalProps) {
  // Stable per link so a re-render never reloads the widget under someone mid-booking.
  const src = useMemo(() => {
    if (!link) return null
    let tagged: string
    try {
      const url = new URL(link)
      url.searchParams.set('utm_source', UTM_SOURCE)
      if (sessionId) url.searchParams.set('utm_content', sessionId)
      tagged = url.toString()
    } catch (error) {
      tagged = link
    }
    return buildCalendlyUrl(tagged)
  }, [link, sessionId])

  useCalendlyMessages((event, payload) => {
    if (!link || event !== 'calendly.event_scheduled') return

    const eventUri: string | null = payload?.event?.uri ?? null
    const inviteeUri: string | null = payload?.invitee?.uri ?? null

    segmentTrack('Chat Quote - Meeting Scheduled', {
      service,
      provider: 'n8n',
      sessionId,
      anonymousId: getAnonymousId(),
      eventId: eventIdFromUri(eventUri),
    })

    reportChatMeetingBooked({ sessionId, eventUri, inviteeUri })
  })

  return (
    <Dialog show={Boolean(link)} onHide={onClose}>
      <div
        role='dialog'
        aria-modal='true'
        aria-labelledby='chat-calendly-heading'
        className={classes.container}
      >
        <button type='button' className={classes.close} aria-label='Close' onClick={onClose}>
          ×
        </button>
        <h2 id='chat-calendly-heading' className={classes.heading}>
          Book a quote review call
        </h2>
        <p className={classes.sub}>Pick a time with your account manager.</p>
        {src && (
          <div className={classes.embed}>
            <iframe
              title='Book a quote review call'
              src={src}
              className={classes.frame}
              height={CALENDLY_HEIGHT}
              allow='camera; microphone; fullscreen'
            />
          </div>
        )}
      </div>
    </Dialog>
  )
}
